import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, RefreshCcw, Edit3, Hourglass, FolderOpen, Loader2, Database, ChevronDown, Check } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';
import MultiSelectApiDropdownWithAll from '../../../components/MultiSelectApiDropdownWithAll';

interface DatabasesViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;

}

interface CustomSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: CustomSelectOption[];
  icon?: React.ReactNode;
  minWidth?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  icon,
  minWidth = '130px',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 140),
      });
    }
  }, []);

  const toggleOpen = () => {
    if (!isOpen) {
      updateCoords();
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleScrollOrResize = () => {
      if (isOpen) {
        updateCoords();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleScrollOrResize);
      window.addEventListener('scroll', handleScrollOrResize, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [isOpen, updateCoords]);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        style={{ minWidth }}
        className="flex items-center justify-between gap-1.5 bg-gray-900/90 hover:bg-gray-800/80 border border-gray-700/80 rounded-lg px-2 py-1 text-[10px] text-gray-200 font-semibold focus:outline-none focus:border-orange-500 transition-all cursor-pointer shadow-sm h-7"
      >
        <div className="flex items-center gap-1 truncate min-w-0">
          {selectedOption?.icon || icon}
          <span className="truncate">{selectedOption?.label || value}</span>
        </div>
        <ChevronDown className={`w-3 h-3 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
              zIndex: 99999,
            }}
            className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl overflow-hidden py-1 max-h-60 overflow-y-auto custom-scroll animate-in fade-in slide-in-from-top-1 duration-150"
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-2.5 py-1 text-[11px] font-semibold flex items-center justify-between gap-2 cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-orange-500/15 text-orange-400 font-bold'
                      : 'text-gray-200 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-1.5 truncate min-w-0">
                    {opt.icon}
                    <span className="truncate">{opt.label}</span>
                  </div>
                  {isSelected && <Check className="w-3 h-3 text-orange-400 shrink-0" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-200 text-[9px] truncate max-w-[130px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const DatabasesView: React.FC<DatabasesViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);
  const [commitsData, setCommitsData] = useState<any>(null);
  const [connectionsData, setConnectionsData] = useState<any>(null);
  const [rowsData, setRowsData] = useState<any>(null);
  const [txnsData, setTxnsData] = useState<any>(null);
  const [selectedDatabases, setSelectedDatabases] = useState<any[]>([{ id: '*', name: 'Select all' }]);

  // Per-chart Database Filter States
  const [selectedCommitsDb, setSelectedCommitsDb] = useState<string>('ALL');
  const [selectedRollbacksDb, setSelectedRollbacksDb] = useState<string>('ALL');
  const [selectedConnsDb, setSelectedConnsDb] = useState<string>('ALL');
  const [selectedRowsDb, setSelectedRowsDb] = useState<string>('ALL');
  const [selectedMaxTxnDb, setSelectedMaxTxnDb] = useState<string>('ALL');
  const [selectedOpenTxnDb, setSelectedOpenTxnDb] = useState<string>('ALL');

  // Legend Hover States
  const [hoveredCommits, setHoveredCommits] = useState<string | null>(null);
  const [hoveredRollbacks, setHoveredRollbacks] = useState<string | null>(null);
  const [hoveredConns, setHoveredConns] = useState<string | null>(null);
  const [hoveredRows, setHoveredRows] = useState<string | null>(null);
  const [hoveredMaxTxn, setHoveredMaxTxn] = useState<string | null>(null);
  const [hoveredOpenTxn, setHoveredOpenTxn] = useState<string | null>(null);

  const availableDatabases = useMemo(() => {
    const set = new Set<string>();
    (commitsData?.databases || []).forEach((db: string) => set.add(db));
    (connectionsData?.databases || []).forEach((db: string) => set.add(db));
    (rowsData?.databases || []).forEach((db: string) => set.add(db));
    (txnsData?.databases || []).forEach((db: string) => set.add(db));
    return Array.from(set);
  }, [commitsData, connectionsData, rowsData, txnsData]);

  const ChartDbDropdown = ({
    value,
    onChange,
    options,
  }: {
    value: string;
    onChange: (val: string) => void;
    options: string[];
  }) => {
    const dbList = options && options.length > 0 ? options : availableDatabases;
    if (dbList.length === 0) return null;

    const selectOptions: CustomSelectOption[] = [
      { value: 'ALL', label: 'All Databases' },
      ...dbList.map((db) => ({ value: db, label: db })),
    ];

    return <CustomSelect value={value} onChange={onChange} options={selectOptions} minWidth="130px" />;
  };

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60');

    const baseParams: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) baseParams.client_id = clientId;

    const dbNames = selectedDatabases
      .map((item) => (typeof item === 'string' ? item : item?.id || item?.name || item?.database))
      .filter((name) => name && name !== '*');

    if (dbNames.length > 0) {
      baseParams.database = dbNames.join(',');
    }

    const safeGet = async (url: string, params: any) => {
      try {
        const res = await baseapi.get(url, { params });
        return res.data?.data || res.data;
      } catch (e) {
        return null;
      }
    };

    try {
      const [cRes, connRes, rRes, tRes] = await Promise.all([
        safeGet('/db_monitor_dashboard/graph/per_db/commits', baseParams),
        safeGet('/db_monitor_dashboard/graph/per_db/connections', baseParams),
        safeGet('/db_monitor_dashboard/graph/per_db/rows', baseParams),
        safeGet('/db_monitor_dashboard/graph/per_db/transactions', baseParams),
      ]);

      setCommitsData(cRes);
      setConnectionsData(connRes);
      setRowsData(rRes);
      setTxnsData(tRes);
    } catch (err) {
      console.warn('DatabasesView fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId, selectedDatabases]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const formatTimeLabels = (labels: string[]) => {
    if (!labels || labels.length === 0) return [];
    const minutes = parseInt(timeRange || '60');
    const seen: { [key: string]: number } = {};
    return labels.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const formatSeries = (labels: string[], databases: string[], seriesMap: any, metricKey: string) => {
    if (!labels || labels.length === 0 || !databases) return [];
    const timeLabels = formatTimeLabels(labels);
    return labels.map((_, idx) => {
      const point: any = { time: timeLabels[idx] };
      databases.forEach((db) => {
        const val = seriesMap?.[`${db}:${metricKey}`]?.[idx];
        point[db] = val !== undefined && val !== null ? Number(val.toFixed(2)) : 0;
      });
      return point;
    });
  };

  const commitsFormatted = formatSeries(commitsData?.labels || [], commitsData?.databases || [], commitsData?.series || {}, 'commits');
  const rollbacksFormatted = formatSeries(commitsData?.labels || [], commitsData?.databases || [], commitsData?.series || {}, 'rollbacks');
  const connectionsFormatted = formatSeries(connectionsData?.labels || [], connectionsData?.databases || [], connectionsData?.series || {}, 'connections');
  const maxTxnFormatted = formatSeries(txnsData?.labels || [], txnsData?.databases || [], txnsData?.series || {}, 'max_txn_time_sec');
  const openTxnFormatted = formatSeries(txnsData?.labels || [], txnsData?.databases || [], txnsData?.series || {}, 'open_txns');

  const rowsTimeLabels = formatTimeLabels(rowsData?.labels || []);
  const getRowsFormatted = (selectedDb: string) => {
    return (rowsData?.labels || []).map((_: string, idx: number) => {
      const point: any = { time: rowsTimeLabels[idx] };
      ['rows_inserted', 'rows_updated', 'rows_deleted'].forEach((op) => {
        let sum = 0;
        const dbsToInclude = selectedDb === 'ALL'
          ? (rowsData?.databases || [])
          : (rowsData?.databases || []).filter((db: string) => db === selectedDb);

        dbsToInclude.forEach((db: string) => {
          const val = (rowsData?.series?.[`${db}:${op}`] || [])[idx];
          if (val !== undefined && val !== null) {
            sum += Number(val);
          }
        });
        let name = 'Inserted';
        if (op === 'rows_updated') name = 'Updated';
        if (op === 'rows_deleted') name = 'Deleted';
        point[name] = Number(sum.toFixed(2));
      });
      return point;
    });
  };
  const rowsFormatted = getRowsFormatted(selectedRowsDb);

  const colors = ['#3b82f6', '#10b981', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4'];
  const hasData = (arr: any[]) => arr && arr.length > 0;

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  const getDot = (_hovered?: any, _key?: any, _color?: any, _defaultR?: any) => false;

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    )
  }
  return (
    <div className="space-y-3 relative">

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
            <Database className="w-4 h-4" />
          </div>
          <h2 className="text-lg font-bold text-white">Database Performance</h2>
        </div>

        {/* Database Multi-Select Dropdown */}
        <div className="w-full sm:w-72">
          <MultiSelectApiDropdownWithAll
            placeholder="Select Databases"
            apiEndpoint="/db_monitor_dashboard/databases"
            additionalParams={{
              cluster_id: activeCluster,
              ...(clientId ? { client_id: clientId } : {}),
            }}
            displayProperty="name"
            idProperty="id"
            searchFields={['name']}
            value={selectedDatabases}
            onChange={(items) => setSelectedDatabases(items)}
            selectAll={true}
            buttonHeight={40}
          />
        </div>
      </div>

      {/* Row 1: 2 Graphs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        {/* Graph 1: Commits */}
        <div className="">
          <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <div className="flex items-center gap-2 truncate">
              <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider truncate">Commits per Database</h4>
            </div>
            <ChartDbDropdown value={selectedCommitsDb} onChange={setSelectedCommitsDb} options={commitsData?.databases} />
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(commitsFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={commitsFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredCommits} onHoverSeries={setHoveredCommits} />} />
                  {(() => {
                    const allDbKeys = Object.keys(commitsFormatted[0] || {}).filter((k) => k !== 'time');
                    return allDbKeys
                      .filter((k) => selectedCommitsDb === 'ALL' || k === selectedCommitsDb)
                      .map((dbKey, i) => {
                        const origIndex = allDbKeys.indexOf(dbKey);
                        const colorIndex = origIndex >= 0 ? origIndex : i;
                        const color = colors[colorIndex % colors.length];
                        return (
                          <Line
                            key={dbKey}
                            type="monotone"
                            dataKey={dbKey}
                            stroke={color}
                            strokeOpacity={getOpacity(hoveredCommits, dbKey)}
                            strokeWidth={hoveredCommits === dbKey ? 2.5 : 1.5}
                            dot={getDot(hoveredCommits, dbKey, color)}
                          />
                        );
                      });
                  })()}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No commits data available</span>
            )}
          </div>
        </div>

        {/* Graph 2: Rollbacks */}
        <div className="">
          <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <div className="flex items-center gap-2 truncate">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider truncate">Rollbacks per Database</h4>
            </div>
            <ChartDbDropdown value={selectedRollbacksDb} onChange={setSelectedRollbacksDb} options={commitsData?.databases} />
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(rollbacksFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rollbacksFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredRollbacks} onHoverSeries={setHoveredRollbacks} />} />
                  {(() => {
                    const allDbKeys = Object.keys(rollbacksFormatted[0] || {}).filter((k) => k !== 'time');
                    return allDbKeys
                      .filter((k) => selectedRollbacksDb === 'ALL' || k === selectedRollbacksDb)
                      .map((dbKey, i) => {
                        const origIndex = allDbKeys.indexOf(dbKey);
                        const colorIndex = origIndex >= 0 ? origIndex : i;
                        const color = colors[(colorIndex + 3) % colors.length];
                        return (
                          <Line
                            key={dbKey}
                            type="monotone"
                            dataKey={dbKey}
                            stroke={color}
                            strokeOpacity={getOpacity(hoveredRollbacks, dbKey)}
                            strokeWidth={hoveredRollbacks === dbKey ? 2.5 : 1.5}
                            dot={getDot(hoveredRollbacks, dbKey, color)}
                          />
                        );
                      });
                  })()}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No rollbacks data available</span>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: 2 Graphs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        {/* Graph 3: Active Sessions */}
        <div className="">
          <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <div className="flex items-center gap-2 truncate">
              <RefreshCcw className="w-4 h-4 text-blue-400 shrink-0" />
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider truncate">Active Sessions per DB</h4>
            </div>
            <ChartDbDropdown value={selectedConnsDb} onChange={setSelectedConnsDb} options={connectionsData?.databases} />
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(connectionsFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={connectionsFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredConns} onHoverSeries={setHoveredConns} />} />
                  {(() => {
                    const allDbKeys = Object.keys(connectionsFormatted[0] || {}).filter((k) => k !== 'time');
                    return allDbKeys
                      .filter((k) => selectedConnsDb === 'ALL' || k === selectedConnsDb)
                      .map((dbKey, i) => {
                        const origIndex = allDbKeys.indexOf(dbKey);
                        const colorIndex = origIndex >= 0 ? origIndex : i;
                        const color = colors[colorIndex % colors.length];
                        return (
                          <Line
                            key={dbKey}
                            type="monotone"
                            dataKey={dbKey}
                            stroke={color}
                            strokeOpacity={getOpacity(hoveredConns, dbKey)}
                            strokeWidth={hoveredConns === dbKey ? 2.5 : 1.5}
                            dot={getDot(hoveredConns, dbKey, color)}
                          />
                        );
                      });
                  })()}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No active session data available</span>
            )}
          </div>
        </div>

        {/* Graph 4: Rows Modified */}
        <div className="">
          <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <div className="flex items-center gap-2 truncate">
              <Edit3 className="w-4 h-4 text-amber-400 shrink-0" />
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider truncate">
                Rows Modified
              </h4>
            </div>
            <ChartDbDropdown value={selectedRowsDb} onChange={setSelectedRowsDb} options={rowsData?.databases} />
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(rowsFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rowsFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredRows} onHoverSeries={setHoveredRows} />} />
                  <Line
                    type="monotone"
                    dataKey="Inserted"
                    stroke="#10b981"
                    strokeOpacity={getOpacity(hoveredRows, 'Inserted')}
                    strokeWidth={hoveredRows === 'Inserted' ? 2.5 : 1.5}
                    dot={getDot(hoveredRows, 'Inserted', '#10b981')}
                  />
                  <Line
                    type="monotone"
                    dataKey="Updated"
                    stroke="#f59e0b"
                    strokeOpacity={getOpacity(hoveredRows, 'Updated')}
                    strokeWidth={hoveredRows === 'Updated' ? 2.5 : 1.5}
                    dot={getDot(hoveredRows, 'Updated', '#f59e0b')}
                  />
                  <Line
                    type="monotone"
                    dataKey="Deleted"
                    stroke="#ef4444"
                    strokeOpacity={getOpacity(hoveredRows, 'Deleted')}
                    strokeWidth={hoveredRows === 'Deleted' ? 2.5 : 1.5}
                    dot={getDot(hoveredRows, 'Deleted', '#ef4444')}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No row modification data available</span>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: 2 Graphs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        {/* Graph 5: Max Txn Time */}
        <div className="">
          <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <div className="flex items-center gap-2 truncate">
              <Hourglass className="w-4 h-4 text-purple-400 shrink-0" />
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider truncate">Max Txn Time by DB (sec)</h4>
            </div>
            <ChartDbDropdown value={selectedMaxTxnDb} onChange={setSelectedMaxTxnDb} options={txnsData?.databases} />
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(maxTxnFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={maxTxnFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredMaxTxn} onHoverSeries={setHoveredMaxTxn} />} />
                  {(() => {
                    const allDbKeys = Object.keys(maxTxnFormatted[0] || {}).filter((k) => k !== 'time');
                    return allDbKeys
                      .filter((k) => selectedMaxTxnDb === 'ALL' || k === selectedMaxTxnDb)
                      .map((dbKey, i) => {
                        const origIndex = allDbKeys.indexOf(dbKey);
                        const colorIndex = origIndex >= 0 ? origIndex : i;
                        const color = colors[(colorIndex + 2) % colors.length];
                        return (
                          <Line
                            key={dbKey}
                            type="monotone"
                            dataKey={dbKey}
                            stroke={color}
                            strokeOpacity={getOpacity(hoveredMaxTxn, dbKey)}
                            strokeWidth={hoveredMaxTxn === dbKey ? 2.5 : 1.5}
                            dot={getDot(hoveredMaxTxn, dbKey, color)}
                          />
                        );
                      });
                  })()}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No transaction time data available</span>
            )}
          </div>
        </div>

        {/* Graph 6: Open Transactions */}
        <div className="">
          <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <div className="flex items-center gap-2 truncate">
              <FolderOpen className="w-4 h-4 text-blue-400 shrink-0" />
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider truncate">Open Transactions by DB</h4>
            </div>
            <ChartDbDropdown value={selectedOpenTxnDb} onChange={setSelectedOpenTxnDb} options={txnsData?.databases} />
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(openTxnFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={openTxnFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredOpenTxn} onHoverSeries={setHoveredOpenTxn} />} />
                  {(() => {
                    const allDbKeys = Object.keys(openTxnFormatted[0] || {}).filter((k) => k !== 'time');
                    return allDbKeys
                      .filter((k) => selectedOpenTxnDb === 'ALL' || k === selectedOpenTxnDb)
                      .map((dbKey, i) => {
                        const origIndex = allDbKeys.indexOf(dbKey);
                        const colorIndex = origIndex >= 0 ? origIndex : i;
                        const color = colors[colorIndex % colors.length];
                        return (
                          <Line
                            key={dbKey}
                            type="monotone"
                            dataKey={dbKey}
                            stroke={color}
                            strokeOpacity={getOpacity(hoveredOpenTxn, dbKey)}
                            strokeWidth={hoveredOpenTxn === dbKey ? 2.5 : 1.5}
                            dot={getDot(hoveredOpenTxn, dbKey, color)}
                          />
                        );
                      });
                  })()}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No open transaction data available</span>
            )}
          </div>
        </div>
      </div>


      {/* {!isES && (
        <div className="border-t border-gray-800 pt-4">
          <IndexesView
            activeCluster={activeCluster}
            timeRange={timeRange}
            clientId={clientId}
            refreshKey={refreshKey}
          />
        </div>
      )} */}

    </div>
  );
};
